import DamuCountdown from 'components/views/DamuCountdown'
import PrescoringLoader from 'components/views/Loaders/PrescoringLoader'
import ScoringLoader from 'components/views/Loaders/ScoringLoader'
import NotaryPage from 'components/views/Notary'
import OpenBorrowerAccount from 'components/views/OpenBorrowerAccount'
import Success from 'components/views/Success/Success'
import { useAppSelector } from 'hooks'
import { useGlobalWsStepHandler } from 'hooks/useGlobalWsStepHandler'
import { useScrollToTopOnNavigate } from 'hooks/useScrollToTopOnNavigate'
import Layout from 'layout'
import LandingLayout from 'layout/LandingLayout'
import ErrorPage from 'pages/404'
import DigitalSignPage from 'pages/Biometry'
import CreateLeadPage from 'pages/CreateLead'
import DigitalDoc from 'pages/DigitalDoc'
import InternalStepLoader from 'pages/InternalStepLoader'
import RepaymentScheduleSign from 'pages/RepaymentScheduleSign'
import { useCallback, useEffect } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useLazyGetApplicationStatusQuery } from 'redux/api'
import { useLazyGetUserQuery } from 'redux/api/leadsApi'
import { getActiveCreditApplication, getCurrentFlowId, getImproveParamsFlows } from 'redux/store/slices/userSlice'
import ProtectedRoute from 'router/ProtectedRoute'
import { ApplicationEvents } from 'share/enums/events'
import { pathnames } from 'utils/constants'
import Emitter from 'utils/emitter'
import { getSourceType } from 'utils/getSourceType'
import { getRouteByStatus } from 'utils/routing/routing-helper'

import { useSaveParamsPartner } from '../hooks/saveParamsPartner'
import { useSuperAppAuth } from '../hooks/useSuperAppAuth'
import CertificationCenterPage from './CertificationCenter'
import DamuSecondConfirmationPage from './DamuSecondConfirmation'
import DamuVerification from './DamuVerification'
import DecisionPage from './Decision'
import DocumentsSignPage from './DocumentsSign'
import IndexPage from './Landing'
import OwnDebt from './ownDebt'
import Payment from './Payment'
import Q19CallPage from './Q19CallPage'
import Rejected from './Rejected'
import RetryStepPage from './RetryStepPage'
import SpouseConfirmation from './SpouseConfirmation'
import SuperappCalculator from './SuperappCalculator'
import UpgradeConditionsPage from './UpgradeConditionsPage'

const Application = () => {
  // Tools
  const navigate = useNavigate()
  const { search } = useLocation()

  // Selectors
  const activeCreditApplication = useAppSelector(getActiveCreditApplication)
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const improveParamFlows = useAppSelector(getImproveParamsFlows)

  // Mutations
  const [fetchStatus] = useLazyGetApplicationStatusQuery()
  const [fetchUser] = useLazyGetUserQuery()

  const sourceType = getSourceType()

  // Handlers
  const fetchStatusAndNavigate = useCallback(async () => {
    if (!currentFlowId) return
    const response = await fetchStatus(currentFlowId).unwrap()
    navigate(`${getRouteByStatus(response, improveParamFlows)}${search}`, { replace: true })
  }, [currentFlowId, fetchStatus, improveParamFlows, navigate, search])

  const fetchFlowIdsAndNavigate = useCallback(async () => {
    const { current_flow_id, improve_params_flows } = await fetchUser().unwrap()
    const response = await fetchStatus(current_flow_id).unwrap()
    navigate(`${getRouteByStatus(response, improve_params_flows)}${search}`, { replace: true })
  }, [fetchStatus, fetchUser, navigate, search])

  // Hooks
  useScrollToTopOnNavigate()
  useSuperAppAuth()
  useSaveParamsPartner()
  useGlobalWsStepHandler({
    onStepError: fetchStatusAndNavigate
  })

  // Effects
  useEffect(() => {
    fetchStatusAndNavigate()
    // TODO: Отрефакторить здесь

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCreditApplication])

  useEffect(() => {
    Emitter.on(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE, fetchStatusAndNavigate)
    Emitter.on(ApplicationEvents.FETCH_FLOW_IDS_AND_NAVIGATE, fetchFlowIdsAndNavigate)
    return () => {
      Emitter.off(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE, fetchStatusAndNavigate)
      Emitter.off(ApplicationEvents.FETCH_FLOW_IDS_AND_NAVIGATE, fetchFlowIdsAndNavigate)
    }
  }, [fetchFlowIdsAndNavigate, fetchStatusAndNavigate])

  return (
    <Routes>
      <Route path={pathnames.all} element={<ErrorPage />} />
      {sourceType === 'super_app' ? (
        <Route path={pathnames.index} element={<SuperappCalculator />} />
      ) : (
        <Route element={<LandingLayout />}>
          <Route path={pathnames.index} element={<IndexPage />} />
        </Route>
      )}
      <Route element={<Layout />}>
        <Route path={pathnames['create-lead']} element={<CreateLeadPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path={pathnames['prescoring-loader']} element={<PrescoringLoader />} />
          <Route path={pathnames['scoring-loader']} element={<ScoringLoader />} />
          <Route path={pathnames.biometry} element={<DigitalSignPage />} />
          <Route path={pathnames.decision} element={<DecisionPage />} />
          <Route path={pathnames.notary} element={<NotaryPage />} />
          <Route path={pathnames['q19-call']} element={<Q19CallPage />} />
          <Route path={pathnames['documents-sign']} element={<DocumentsSignPage />} />
          <Route path={pathnames['upgrade-conditions']} element={<UpgradeConditionsPage />} />
          <Route path={pathnames['repayment-schedule-sign']} element={<RepaymentScheduleSign />} />
          <Route path={pathnames['digital-doc']} element={<DigitalDoc />} />
          <Route path={pathnames['own-debt']} element={<OwnDebt />} />
          <Route path={pathnames['certification-center']} element={<CertificationCenterPage />} />
          <Route path={pathnames['internal-step-loader']} element={<InternalStepLoader />} />
          <Route path={pathnames['open-borrower-account']} element={<OpenBorrowerAccount />} />
          <Route path={pathnames['damu-countdown']} element={<DamuCountdown />} />
          <Route path={pathnames['damu-verification']} element={<DamuVerification />} />
          <Route path={pathnames['damu-second-confirmation']} element={<DamuSecondConfirmationPage />} />
          <Route path={pathnames['pledge-fee-payment']} element={<Payment />} />
          <Route path={pathnames['spouse-confirmation']} element={<SpouseConfirmation />} />
          <Route path={pathnames.rejected} element={<Rejected />} />
          <Route path={pathnames.retry} element={<RetryStepPage />} />
          <Route path={pathnames.success} element={<Success />} />
        </Route>
      </Route>
    </Routes>
  )
}

export default Application
